import * as React from 'react';
import { NextPage } from 'next';
import { Dispatch } from 'redux';
import { useRouter } from 'next/router';
import Head from 'next/head';
import * as url from 'url';

import { ApiKeyContext } from 'contexts/ApiKeyContext';
import { PmpProductDetails } from 'components/PrivateMarketplace/PmpProductDetails/PmpProductDetails';
import { PrivateMarketplaceLayout } from 'components/PrivateMarketplaceLayout/PrivateMarketplaceLayout';
import { wrapper } from 'ducks';
import {
  fetchSettingsServerSide,
  fetchProductServerSide,
  fetchReservationServerSide,
  fetchCustomPagesServerSide,
  fetchCustomTopPageServerSide,
} from 'ducks/server';
import { fetchReviews } from 'ducks/universal/reviews';
import { fetchConfigForHostname } from 'lib/util/fetchConfigForHostname';
import { getI18nServerSideProps } from 'i18n';
import { withSSR } from 'withSSR';
import { absoluteUrl } from 'lib/util/absoluetUrl';
import { CurrentUrlContext } from 'contexts/CurrentUrlContext';
import { languageOptionMap } from 'lib/util/i18nHelpers';

interface Props {
  apiKey: string;
  host: string;
  ogpPromotionId: string;
  showOgpHighlight: boolean;
}

const Product: NextPage<Props> = ({ apiKey, host, ogpPromotionId, showOgpHighlight }) => {
  const router = useRouter();
  const url = `${host}${router.asPath}`;

  return (
    <ApiKeyContext.Provider
      value={{
        apiKey,
        useApiKeyInPaths: false,
      }}
    >
      <CurrentUrlContext.Provider
        value={{
          url,
        }}
      >
        <Head>
          <meta property="og:url" content={url} />
          <meta property="og:type" content={'article'} />
          <meta name="twitter:card" content={'summary_large_image'} />
        </Head>
        <PrivateMarketplaceLayout>
          <PmpProductDetails
            host={host}
            ogpPromotionId={ogpPromotionId}
            showOgpHighlight={showOgpHighlight}
          />
        </PrivateMarketplaceLayout>
      </CurrentUrlContext.Provider>
    </ApiKeyContext.Provider>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  async ({ params, query, req, store }) => {
    const hostname = req.headers?.host?.split(':')[0] || '';
    const { apiKey, lang } = await fetchConfigForHostname(hostname);

    const queries = url.parse(req.url || '', true).query;

    const productId = params?.id as string;
    const reservationId = query?.r as string;

    const dispatch: Dispatch<any> = store.dispatch;
    await Promise.all([
      dispatch(fetchSettingsServerSide(apiKey, '', lang, `https://${hostname}`)),
      dispatch(fetchProductServerSide(apiKey, productId, lang)),
      dispatch(
        fetchReviews(apiKey, lang, {
          productIds: [productId],
          pageSize: 5,
        })
      ),
      dispatch(fetchCustomPagesServerSide(apiKey, lang)),
      ...(reservationId ? [dispatch(fetchReservationServerSide(apiKey, reservationId, lang))] : []),
    ]);

    const isCustomTopPageEnabled = store.getState().server.settings.all?.is_custom_top_page_enabled;

    if (isCustomTopPageEnabled) {
      await dispatch(fetchCustomTopPageServerSide(apiKey, lang));
    }

    const i18nProps = await getI18nServerSideProps(lang);

    const product = store.getState().server.product.product;
    if (
      product?.booking_widget_settings?.blacklisted_languages?.some(
        (language) => languageOptionMap[language as keyof typeof languageOptionMap]?.iso === lang
      ) ||
      !product?.booking_widget_settings?.is_visible
    ) {
      return {
        redirect: {
          destination: '/top',
          permanent: false,
        },
      };
    }

    (req as any).locale = lang;

    const ogpPromotionId = queries?.promo && typeof queries.promo === 'string' ? queries.promo : '';
    const showOgpHighlight = typeof queries?.highlight === 'string';

    return {
      props: {
        apiKey,
        host: absoluteUrl(req),
        ogpPromotionId,
        showOgpHighlight,
        ...i18nProps,
      },
    };
  }
);

export default withSSR()(Product);
